import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useSnackbar } from "notistack";
import { twoFactorTimerUtils } from "../utils/twoFactorTimer";

/**
 * Custom hook for managing two-factor authentication timer
 * Handles timer state, automatic cleanup, and timeout actions
 */
export const useTwoFactorTimer = () => {
  const [remainingTime, setRemainingTime] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const intervalRef = useRef(null);

  /**
   * Start the two-factor timer
   */
  const startTimer = useCallback(() => {
    console.log("Starting two-factor timer");
    twoFactorTimerUtils.startTimer();
    setIsActive(true);
  }, []);

  /**
   * Clear the timer and reset state
   */
  const clearTimer = useCallback(() => {
    console.log("Clearing two-factor timer");
    twoFactorTimerUtils.clearTimer();
    setIsActive(false);
    setRemainingTime(null);

    // Clear the interval if it exists
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Handle timeout - clear state and redirect to login
   */
  const handleTimeout = useCallback(() => {
    console.log("Two-factor authentication session expired");

    // Clear 2FA state in Redux
    dispatch({ type: "SET_LOGIN_RESPONSE_FOR_TWOFACTOR", payload: null });
    dispatch({ type: "NATIVE_LOGIN", payload: null });

    // Clear timer
    clearTimer();

    // Show notification to user
    enqueueSnackbar(
      "Two-factor authentication session expired. Please log in again.",
      {
        variant: "warning",
      }
    );

    // Redirect to login page
    dispatch(push("/login"));
  }, [dispatch, clearTimer, enqueueSnackbar]);

  /**
   * Check timer status and update remaining time
   */
  const checkTimer = useCallback(() => {
    if (twoFactorTimerUtils.isExpired()) {
      handleTimeout();
      return;
    }

    const remaining = twoFactorTimerUtils.getRemainingTime();
    if (remaining !== null) {
      setRemainingTime(remaining);
      setIsActive(true);
    } else {
      setIsActive(false);
      setRemainingTime(null);
    }
  }, [handleTimeout]);

  /**
   * Initialize timer on mount and set up interval
   */
  useEffect(() => {
    // Initial check when hook mounts - check for existing timer first
    if (twoFactorTimerUtils.hasActiveTimer()) {
      if (twoFactorTimerUtils.isExpired()) {
        console.log("Timer expired on mount, handling timeout");
        handleTimeout();
        return;
      } else {
        console.log("Resuming existing timer from localStorage");
        const remaining = twoFactorTimerUtils.getRemainingTime();
        setRemainingTime(remaining);
        setIsActive(true);
      }
    }

    // Set up interval to check timer status every second
    if (isActive) {
      intervalRef.current = setInterval(checkTimer, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup interval on unmount or when isActive changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, checkTimer, handleTimeout]);

  /**
   * Format remaining time for display
   */
  const formattedTime = remainingTime
    ? twoFactorTimerUtils.formatTime(remainingTime)
    : "0:00";

  return {
    /** Remaining time in milliseconds */
    remainingTime,
    /** Whether timer is currently active */
    isActive,
    /** Formatted time string for display */
    formattedTime,
    /** Function to start the timer */
    startTimer,
    /** Function to clear the timer */
    clearTimer,
    /** Function to handle timeout manually */
    handleTimeout,
    /** Check if timer exists */
    hasActiveTimer: twoFactorTimerUtils.hasActiveTimer,
    /** Check if timer is expired */
    isExpired: twoFactorTimerUtils.isExpired,
  };
};
